import type { JSX } from 'preact';
import type { Location } from '../domain/types';
import { Field } from './components';

/**
 * Where a class happens.
 *
 * A class needs a location because its timezone comes from one, but making the
 * user create a location first is a dead end: nobody thinks "I must define Studio
 * A" before "I teach Tuesdays at Studio A". So a name can be typed here and the
 * place is created on save, using the phone's own timezone.
 */
export type LocationChoice =
  | { readonly kind: 'existing'; readonly id: string }
  | { readonly kind: 'new'; readonly name: string };

export const NEW_LOCATION_VALUE = '__new__';

export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/**
 * Turns an IANA identifier into something readable: `America/Los_Angeles` becomes
 * "Los Angeles time". The full identifier still appears when editing a place,
 * because correctness depends on it.
 */
export function friendlyZone(timeZone: string): string {
  const city = timeZone.split('/').at(-1) ?? timeZone;
  return `${city.replace(/_/gu, ' ')} time`;
}

export function LocationField(props: {
  locations: readonly Location[];
  value: LocationChoice;
  error?: string | undefined;
  onChange: (choice: LocationChoice) => void;
}): JSX.Element {
  const { locations, value, onChange } = props;

  // With nothing set up yet there is no choice to make, so skip the picker
  // entirely and just ask where the class is.
  if (locations.length === 0) {
    return (
      <Field
        label="Where"
        error={props.error}
        hint={`Times will use ${friendlyZone(browserTimeZone())}.`}
      >
        <input
          type="text"
          placeholder="Studio A"
          value={value.kind === 'new' ? value.name : ''}
          onInput={(event) =>
            onChange({ kind: 'new', name: (event.target as HTMLInputElement).value })
          }
        />
      </Field>
    );
  }

  return (
    <>
      <Field label="Where" error={value.kind === 'existing' ? props.error : undefined}>
        <select
          value={value.kind === 'existing' ? value.id : NEW_LOCATION_VALUE}
          onChange={(event) => {
            const selected = (event.target as HTMLSelectElement).value;
            onChange(
              selected === NEW_LOCATION_VALUE
                ? { kind: 'new', name: '' }
                : { kind: 'existing', id: selected },
            );
          }}
        >
          {locations.map((location) => (
            <option key={location.id} value={location.id}>
              {location.name}
            </option>
          ))}
          <option value={NEW_LOCATION_VALUE}>Somewhere new…</option>
        </select>
      </Field>

      {value.kind === 'new' ? (
        <Field
          label="New place"
          error={props.error}
          hint={`Times will use ${friendlyZone(browserTimeZone())}.`}
        >
          <input
            type="text"
            placeholder="Studio B"
            value={value.name}
            onInput={(event) =>
              onChange({ kind: 'new', name: (event.target as HTMLInputElement).value })
            }
          />
        </Field>
      ) : null}
    </>
  );
}
